import type { UserProfile, UserStatus } from '@/shared/utils/format-status.js';

export type AssignmentResponse = {
    id: string;
    clientId: string;
    clientName: string;
    clientAbbreviation: string;
    clientLogoUrl: string | null;
    projectId: string;
    projectName: string;
    profile: UserProfile;
};

export type AssignmentProps = {
    id: string;
    clientId: string;
    clientName: string;
    clientAbbreviation: string;
    clientLogoUrl: string | null;
    projectId: string;
    projectName: string;
    profile: UserProfile;
};

export class AssignmentModel {
    private constructor(private readonly props: AssignmentProps) {}

    public static with(props: AssignmentProps): AssignmentModel {
        return new AssignmentModel(props);
    }

    public static withFromResponse(response: AssignmentResponse): AssignmentModel {
        return AssignmentModel.with({ ...response });
    }

    public static withFromResponseList(list: AssignmentResponse[]): AssignmentModel[] {
        return list.map(AssignmentModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get clientId(): string {
        return this.props.clientId;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public get clientAbbreviation(): string {
        return this.props.clientAbbreviation;
    }

    public get clientLogoUrl(): string | null {
        return this.props.clientLogoUrl;
    }

    public get projectId(): string {
        return this.props.projectId;
    }

    public get projectName(): string {
        return this.props.projectName;
    }

    public get profile(): UserProfile {
        return this.props.profile;
    }

    public toObject(): AssignmentProps {
        return { ...this.props };
    }
}

export type UserResponse = {
    id: string;
    name: string;
    email: string;
    profile: UserProfile;
    status: UserStatus;
    isGlobalAdmin: boolean;
    avatarUrl?: string | null;
    mustChangePassword: boolean;
    phone?: string | null;
    lastLoginAt?: string | null;
    createdAt: string;
    assignments: AssignmentResponse[];
};

export type UserProps = {
    id: string;
    name: string;
    email: string;
    profile: UserProfile;
    status: UserStatus;
    isGlobalAdmin: boolean;
    avatarUrl: string | null;
    mustChangePassword: boolean;
    phone: string | null;
    lastLoginAt: string | null;
    createdAt: string;
    assignments: AssignmentModel[];
};

export class UserModel {
    private constructor(private readonly props: UserProps) {}

    public static with(props: UserProps): UserModel {
        return new UserModel(props);
    }

    public static withFromResponse(response: UserResponse): UserModel {
        return UserModel.with({
            id: response.id,
            name: response.name,
            email: response.email,
            profile: response.profile,
            status: response.status,
            isGlobalAdmin: response.isGlobalAdmin,
            avatarUrl: response.avatarUrl ?? null,
            mustChangePassword: response.mustChangePassword,
            phone: response.phone ?? null,
            lastLoginAt: response.lastLoginAt ?? null,
            createdAt: response.createdAt,
            assignments: AssignmentModel.withFromResponseList(response.assignments ?? [])
        });
    }

    public static withFromResponseList(list: UserResponse[]): UserModel[] {
        return list.map(UserModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get email(): string {
        return this.props.email;
    }

    public get profile(): UserProfile {
        return this.props.profile;
    }

    public get status(): UserStatus {
        return this.props.status;
    }

    public get isGlobalAdmin(): boolean {
        return this.props.isGlobalAdmin;
    }

    public get avatarUrl(): string | null {
        return this.props.avatarUrl;
    }

    public get mustChangePassword(): boolean {
        return this.props.mustChangePassword;
    }

    public get phone(): string | null {
        return this.props.phone;
    }

    public get lastLoginAt(): string | null {
        return this.props.lastLoginAt;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get assignments(): AssignmentModel[] {
        return this.props.assignments;
    }

    public toObject(): UserProps {
        return {
            ...this.props,
            assignments: this.props.assignments.map((a) => a.toObject()) as unknown as AssignmentModel[]
        };
    }
}

export type CreateUserParams = {
    name: string;
    email: string;
    phone?: string;
    assignments: SaveAssignmentParams[];
};

export type UpdateUserParams = {
    name: string;
    phone?: string;
};

export type ClientOptionResponse = {
    id: string;
    name: string;
    abbreviation: string;
};

export type ClientOptionProps = {
    id: string;
    name: string;
    abbreviation: string;
};

export class ClientOptionModel {
    private constructor(private readonly props: ClientOptionProps) {}

    public static with(props: ClientOptionProps): ClientOptionModel {
        return new ClientOptionModel(props);
    }

    public static withFromResponse(response: ClientOptionResponse): ClientOptionModel {
        return ClientOptionModel.with({ ...response });
    }

    public static withFromResponseList(list: ClientOptionResponse[]): ClientOptionModel[] {
        return list.map(ClientOptionModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get abbreviation(): string {
        return this.props.abbreviation;
    }
}

export type ProjectOptionResponse = {
    id: string;
    name: string;
    clientId: string;
};

export type ProjectOptionProps = {
    id: string;
    name: string;
    clientId: string;
};

export class ProjectOptionModel {
    private constructor(private readonly props: ProjectOptionProps) {}

    public static with(props: ProjectOptionProps): ProjectOptionModel {
        return new ProjectOptionModel(props);
    }

    public static withFromResponse(response: ProjectOptionResponse): ProjectOptionModel {
        return ProjectOptionModel.with({ ...response });
    }

    public static withFromResponseList(list: ProjectOptionResponse[]): ProjectOptionModel[] {
        return list.map(ProjectOptionModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get clientId(): string {
        return this.props.clientId;
    }
}

export type UserAssignmentResponse = {
    id: string;
    clientId: string;
    projectId?: string;
    profile: UserProfile;
    client: { name: string; abbreviation: string };
    project?: { name: string };
};

export type UserAssignmentProps = {
    id: string;
    clientId: string;
    projectId: string | null;
    profile: UserProfile;
    clientName: string;
    clientAbbreviation: string;
    projectName: string | null;
};

export class UserAssignmentModel {
    private constructor(private readonly props: UserAssignmentProps) {}

    public static with(props: UserAssignmentProps): UserAssignmentModel {
        return new UserAssignmentModel(props);
    }

    public static withFromResponse(response: UserAssignmentResponse): UserAssignmentModel {
        return UserAssignmentModel.with({
            id: response.id,
            clientId: response.clientId,
            projectId: response.projectId ?? null,
            profile: response.profile,
            clientName: response.client.name,
            clientAbbreviation: response.client.abbreviation,
            projectName: response.project?.name ?? null
        });
    }

    public static withFromResponseList(list: UserAssignmentResponse[]): UserAssignmentModel[] {
        return list.map(UserAssignmentModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get clientId(): string {
        return this.props.clientId;
    }

    public get projectId(): string | null {
        return this.props.projectId;
    }

    public get profile(): UserProfile {
        return this.props.profile;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public get clientAbbreviation(): string {
        return this.props.clientAbbreviation;
    }

    public get projectName(): string | null {
        return this.props.projectName;
    }

    public toObject(): UserAssignmentProps {
        return { ...this.props };
    }
}

export type SaveAssignmentParams = {
    clientId: string;
    projectId?: string;
    profile: UserProfile;
    isActive: boolean;
};
